// src/services/api.js
import axios from 'axios';
import { formatApiError } from '@/utils/errorHandler';

// Base API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL || 'https://fokb04ehk5.execute-api.us-east-1.amazonaws.com/dev';

// Maximum number of retries for failed requests
const MAX_RETRIES = 2;

// Retry delay in milliseconds (with exponential backoff)
const getRetryDelay = (retryCount) => Math.pow(2, retryCount) * 1000;

// Statuses that should trigger a retry
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504];

/**
 * POSTs that must not be retried with the same body:
 * - reCAPTCHA tokens are single-use (Google siteverify)
 * - side-effecting creates (Cognito/DynamoDB) are not idempotent
 */
function requestMustNotRetryPost(config) {
  if (!config?.url) return false;
  if (String(config.method || 'get').toLowerCase() !== 'post') return false;
  const path = config.url.split('?')[0];
  if (path.includes('/auth/register')) return true;
  if (path.includes('/auth/login/magic-link/')) return true;
  if (path.includes('/auth/login') && !path.includes('magic-link')) return true;
  if (path.includes('/admin/utilities/investor-invite')) return true;
  if (path.includes('/admin/marketing/contacts/batch-ensure')) return true;
  if (path.includes('/check-recipients')) return true;
  return false;
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Token refresh - returns a promise that resolves to a new token
const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    // Get userId for backend (CRITICAL FIX)
    const userId = localStorage.getItem('userId');
    
    console.log('🔄 TOKEN REFRESH: Starting refresh...');
    console.log('🔑 refreshToken available:', !!refreshToken);
    console.log('👤 userId available:', userId);
    
    if (!userId) {
      console.error('❌ TOKEN REFRESH FAILED: No userId in localStorage!');
      throw new Error('No userId available for token refresh');
    }
    
    // Create a new axios instance without interceptors to prevent infinite loop.
    // Attach the current idToken if available — the endpoint no longer requires it
    // (it authenticates via the refresh token body), but including it avoids a
    // gateway 401 if auth policy ever tightens again.
    const currentIdToken = localStorage.getItem('idToken');
    const refreshApi = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
        ...(currentIdToken ? { Authorization: `Bearer ${currentIdToken}` } : {}),
      }
    });
    
    const response = await refreshApi.post('/auth/token', {
      refreshToken: refreshToken,
      user_id: userId
    });
    
    console.log('✅ TOKEN REFRESH SUCCESS: New tokens received');
    
    if (response.data && response.data.accessToken) {
      // Store both tokens (use accessToken as primary)
      localStorage.setItem('token', response.data.accessToken);
      if (response.data.idToken) {
        localStorage.setItem('idToken', response.data.idToken);
      }
      return response.data.idToken || response.data.accessToken;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Clear all tokens AND userId if refresh fails
    localStorage.removeItem('idToken');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    throw error;
  }
};

// Track if token refresh is in progress
let isRefreshing = false;
// Store for queued requests
let failedQueue = [];

// Process the queued requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    // Add timestamp for debugging/logging in development
    if (process.env.NODE_ENV === 'development') {
      config.metadata = { startTime: new Date().getTime() };
    }
    
    // Add auth token if available - use idToken for Cognito authorization
    const token = localStorage.getItem('idToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params
      });
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    // Log response timing in development
    if (process.env.NODE_ENV === 'development' && response.config.metadata) {
      const duration = new Date().getTime() - response.config.metadata.startTime;
      console.log(`API Response: ${response.config.method.toUpperCase()} ${response.config.url} [${duration}ms]`, {
        status: response.status,
        data: response.data
      });
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, error);
    }
    
    // If retry count exceeds maximum, throw the error
    if (originalRequest._retryCount >= MAX_RETRIES) {
      return Promise.reject(formatApiError(error));
    }
    
    // Handle token refresh for 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If refresh token is available and we're not already refreshing
      if (localStorage.getItem('refreshToken') && !isRefreshing) {
        originalRequest._retry = true;
        isRefreshing = true;
        
        try {
          const newIdToken = await refreshToken();
          isRefreshing = false;
          
          // Update the token in the original request (use idToken)
          originalRequest.headers.Authorization = `Bearer ${newIdToken}`;
          
          // Process queued requests
          processQueue(null, newIdToken);
          
          // Retry the original request
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          
          // Create a more informative error
          const enhancedError = new Error('Session expired. Please log in again.');
          enhancedError.status = 401;
          enhancedError.originalError = refreshError;
          
          return Promise.reject(formatApiError(enhancedError));
        }
      } else if (isRefreshing) {
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(formatApiError(err));
          });
      }
    }
    
    // Handle retries for server errors and network issues
    const baseRetryEligible =
      (!error.response && error.code === 'ECONNABORTED') || // Timeout
      (!error.response && error.message.includes('Network Error')) || // Network error
      (error.response && RETRY_STATUS_CODES.includes(error.response.status)); // Retryable status

    const shouldRetry =
      baseRetryEligible && originalRequest && !requestMustNotRetryPost(originalRequest);

    if (shouldRetry) {
      // Initialize retry count if not already set
      originalRequest._retryCount = originalRequest._retryCount || 0;
      
      // If we haven't exceeded max retries
      if (originalRequest._retryCount < MAX_RETRIES) {
        originalRequest._retryCount++;
        
        // Calculate delay with exponential backoff
        const delay = getRetryDelay(originalRequest._retryCount);
        
        // Return a promise that resolves after the delay
        return new Promise(resolve => {
          setTimeout(() => {
            console.log(`Retrying request [${originalRequest._retryCount}/${MAX_RETRIES}]: ${originalRequest.url}`);
            resolve(api(originalRequest));
          }, delay);
        });
      }
    }
    
    // Return enhanced error for better handling
    return Promise.reject(formatApiError(error));
  }
);

export default api;